import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, Row } from 'react-bootstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Summary } from 'modules/ApprovedLoanInput/Summary';
import { IApprovedLoanInput } from 'modules/ApprovedLoanInput/types';
import {
  calcApprovedLoanValue,
  calcNetDownPayment,
  calcPriceAllowed,
  roundValue,
} from 'modules/ApprovedLoanInput/approvedLoadCalculation.service';
import { PanelWithFields } from 'modules/core/components';
import {
  useDebounceCallback,
  useDebounceVariable,
  useFeesAndTaxesInitialValues,
  useFormEnhanced,
  useSettingsInitialValues,
} from 'modules/core/hooks';
import {
  getApprovedLoanInputInitialValues,
  getApprovedLoanInputReportData,
  getVehiclesByParams,
  getVehiclesByStock,
  getVehicleFetchingFlags,
  getIsTableView,
  getNotAvailableVehicles,
  getNotFoundVehicles,
  getApprovedLoanInputInitialErrors,
} from 'store/selectors/approvedLoanInput.selector';
import { FEES_AND_TAXES_FIELDS } from 'modules/core/shared/feesAndTaxes.config';
import { COMMON_PREFERENCES_FIELDS, PREFERENCES_FIELDS } from 'modules/core/shared/preferencesConfig.const';
import {
  approvedLoanSlice,
  resetByStock,
  resetVehicles,
  resetVehiclesByParams,
  resetVehiclesByStock,
  resetResetCount,
  setIsTableView,
} from 'store/reducers/approvedLoan.reducer';
import { VehicleSearchTable } from 'modules/VehicleSearchTable';
import { AppDispatch } from 'store/store';
import {
  getVehiclesByStockForApprovedLoanInputThunk,
  getVehiclesForApprovedLoanInputThunk,
} from 'actions/vehicleActions';
import { getSubmitVehicleObjectQuery, getTotalVehicles } from 'modules/core/utils';
import { IS_VALID_DELAY, REQUEST_DELAY } from 'modules/core/constants';
import {
  APPROVED_LOAN_INPUT_BY_STOCK_FORM_NAMES_MAPPER,
  APPROVED_LOAN_INPUT_FORM_NAMES_MAPPER,
} from 'modules/core/constants/mappers/submitVehicles.mapper';
import { ColumnWidth, TableName } from 'modules/core/enums';
import { useVendorFieldAndOptions } from 'hooks/useVendorFieldAndOptions';
import { vehicleApi } from 'api';
import { IVehicle } from 'modules/core/types';
import { useSwitchFilters } from 'modules/core/hooks/useSwitchFilters';
import {
  getAddUserVehicleFlags,
  getCreatedUserVehicle,
  getCreatedUserVehicleTableName,
} from 'store/selectors/vehicle.selector';
import { setIsModalsDisabled } from 'store/reducers/ui.reducer';
import { getFeaturesList } from 'store/selectors/ui.selector';
import { useTablePlaceholderType } from 'modules/VehicleSearchTable/hooks';

import {
  APPROVED_LOAN_NAMES_WATCH,
  FIELD_NAMES,
  VEHICLES_COUNT_FIELDS_SUBSCRIPTION,
  DEFAULT_PLACEHOLDER_TITLE,
} from './approvedLoanInput.const';
import { ApprovedLoanCol } from './styles';
import { ApprovedLoanInputService } from './approvedLoanInput.service';
import { FilterView } from '../core/enums/filterView.enum';
import { useHideVehicleField } from '../core/hooks/useHideVehicleField';
import { DisabledModals } from '../core/enums/disabledModals';

export const ApprovedLoanInput: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const addVehicleFlags = useSelector(getAddUserVehicleFlags);
  const createdVehicle = useSelector(getCreatedUserVehicle);
  const { resetCount, isFetchingVehiclesFailed, isFetchingVehicles } = useSelector(
    getVehicleFetchingFlags,
    shallowEqual,
  );
  const isTableView = useSelector(getIsTableView);
  const notAvailableVehicles = useSelector(getNotAvailableVehicles);
  const notFoundVehicles = useSelector(getNotFoundVehicles);
  const vehiclesByParams = useSelector(getVehiclesByParams);
  const vehiclesByStock = useSelector(getVehiclesByStock);
  const createdUserVehicleTableName = useSelector(getCreatedUserVehicleTableName);
  const [showCreatedVehicleErrorMessage, setShowCreatedVehicleErrorMessage] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const { is_calculated_book_value } = useSelector(getFeaturesList);

  const { VENDOR_FILTER_OPTIONS } = useVendorFieldAndOptions();

  if (PREFERENCES_FIELDS[0].controlProps) {
    PREFERENCES_FIELDS[0].controlProps.options = VENDOR_FILTER_OPTIONS;
  }

  const preferencesFields = is_calculated_book_value ? COMMON_PREFERENCES_FIELDS : PREFERENCES_FIELDS;

  const { methods, syncFormAction } = useFormEnhanced<IApprovedLoanInput>(
    approvedLoanSlice.actions.syncInitialValues,
    approvedLoanSlice.actions.syncInitialErrors,
    getApprovedLoanInputInitialValues,
    getApprovedLoanInputInitialErrors,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );

  const {
    watch,
    setValue,
    formState: { isValid },
    getValues,
  } = methods;

  const { feesAndTaxes: feesAndTaxesValues, stocks, filterView, ...primitiveVehiclesCountWatchedValues } = watch(
    VEHICLES_COUNT_FIELDS_SUBSCRIPTION,
  );
  const { tableView, preferences, subvention, subventionYear } = watch();

  const {
    approvedInterestRate,
    approvedMonthlyPayment,
    approvedLoanTerm,
    approvedLTVRatio,
    cashDownPayment,
    tradeValue,
    amountOwed,
    feesAndTaxes,
  } = watch(APPROVED_LOAN_NAMES_WATCH) as IApprovedLoanInput;

  const commonDependencies = [
    approvedMonthlyPayment,
    approvedLoanTerm,
    approvedLTVRatio,
    approvedInterestRate,
    subvention,
    subventionYear,
    cashDownPayment,
    tradeValue,
    amountOwed,
    ...Object.values(preferences),
    ...Object.values(feesAndTaxes),
  ];

  const { currentFilterName, isValidStock, isRequestNeeded } = useSwitchFilters(
    commonDependencies,
    filterView,
    isFetchingVehiclesFailed,
    watch,
    setValue,
  );

  useEffect(() => {
    if (isRequestNeeded) {
      if (filterView === FilterView.BY_PARAMS) dispatch(resetVehiclesByParams());
      else dispatch(resetVehiclesByStock());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterView]);

  useEffect(() => {
    if (!isFetchingVehicles) setIsFetching(false);
  }, [isFetchingVehicles]);

  const vehiclesData: IVehicle[] | undefined = filterView === FilterView.BY_PARAMS ? vehiclesByParams : vehiclesByStock;
  const vehicles = useHideVehicleField(vehiclesData);
  const totalVehicles = getTotalVehicles(filterView, isValidStock, vehicles);

  useEffect(() => {
    if (!isValidStock) dispatch(resetByStock());
  }, [dispatch, isValidStock]);

  useEffect(() => {
    if (!stocks.length && vehicles?.length) dispatch(resetByStock());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, vehicles?.length]);

  useSettingsInitialValues(setValue, getApprovedLoanInputInitialValues);
  useFeesAndTaxesInitialValues(setValue, getApprovedLoanInputInitialValues);

  const onSubmit = useCallback(() => {
    const values = getValues() as IApprovedLoanInput;
    const isFilterByParams = values.filterView === FilterView.BY_PARAMS;

    const CURRENT_APPROVED_LOAN_INPUT_FORM_NAMES_MAPPER = isFilterByParams
      ? APPROVED_LOAN_INPUT_FORM_NAMES_MAPPER
      : APPROVED_LOAN_INPUT_BY_STOCK_FORM_NAMES_MAPPER;

    if (typeof values.vehicleFilters.vehicleType === 'string') {
      values.condition = values.vehicleFilters.vehicleType;
    }

    // eslint-disable-next-line no-param-reassign
    if (!isFilterByParams) values.vehicleFilters.minMonthlyPayment = 0;

    const vehicleParameters = getSubmitVehicleObjectQuery<IApprovedLoanInput>(
      values,
      ApprovedLoanInputService.getSubmitQuery,
      CURRENT_APPROVED_LOAN_INPUT_FORM_NAMES_MAPPER,
    );

    syncFormAction();

    if (isFilterByParams) dispatch(getVehiclesForApprovedLoanInputThunk(vehicleParameters));
    else dispatch(getVehiclesByStockForApprovedLoanInputThunk(vehicleParameters));
  }, [dispatch, syncFormAction, getValues]);

  const { debounced: onSubmitDebounced } = useDebounceCallback((values: unknown) => {
    onSubmit();
  }, REQUEST_DELAY);

  const handleSearchSubmit = useCallback(() => {
    if (isValid) onSubmit();
  }, [isValid, onSubmit]);

  const approvedLoanValue = useMemo(
    () =>
      calcApprovedLoanValue({
        approvedInterestRate,
        approvedMonthlyPayment,
        approvedLoanTerm,
      } as IApprovedLoanInput),
    [approvedInterestRate, approvedLoanTerm, approvedMonthlyPayment],
  );

  const priceAllowedValue = useMemo(
    () =>
      calcPriceAllowed(
        { cashDownPayment, amountOwed, tradeValue, feesAndTaxes } as IApprovedLoanInput,
        approvedLoanValue,
      ),
    [amountOwed, approvedLoanValue, cashDownPayment, feesAndTaxes, tradeValue],
  );

  const netDownPayment = useMemo(
    () => calcNetDownPayment({ cashDownPayment, tradeValue, amountOwed } as IApprovedLoanInput),
    [amountOwed, cashDownPayment, tradeValue],
  );

  // useDebounceVariable hook is used to fix isValid variable state behavior
  const debouncedIsValid = useDebounceVariable(isValid, IS_VALID_DELAY);

  const areRequiredFieldsFilledIn = Boolean(
    approvedMonthlyPayment &&
      approvedLoanTerm &&
      !Number.isNaN(approvedInterestRate) &&
      approvedLTVRatio &&
      !Number.isNaN(feesAndTaxes.salesTaxRate),
  );

  const previewPlaceholder = t('components.approvedLoanInput.previewPlaceholderPostfix', {
    totalVehicles,
    count: totalVehicles,
  });

  const tablePlaceholderType = useTablePlaceholderType(
    areRequiredFieldsFilledIn,
    vehicles || [],
    getVehicleFetchingFlags,
    isValidStock,
    isFetching,
  );

  useEffect(() => {
    if (!!resetCount && !areRequiredFieldsFilledIn) return;

    if (
      debouncedIsValid &&
      areRequiredFieldsFilledIn &&
      (isRequestNeeded || addVehicleFlags.isLoaded) &&
      isValidStock
    ) {
      setIsFetching(true);
      const values = getValues();

      vehicleApi.cancelGetVehiclesRequests();
      vehicleApi.cancelGetVehiclesByStockRequests();
      onSubmitDebounced(values);
      setValue(currentFilterName, false);

      if (addVehicleFlags.isLoaded) setShowCreatedVehicleErrorMessage(true);
    }
  }, [
    addVehicleFlags.isLoaded,
    currentFilterName,
    setValue,
    isRequestNeeded,
    isValidStock,
    resetCount,
    debouncedIsValid,
    onSubmitDebounced,
    areRequiredFieldsFilledIn,
    getValues,
    tableView,
    stocks.length,
    filterView,
    // Inserting the commonVehiclesCountValues object initiates a comparison by reference and an additional hook launch.
    // This technique is used to avoid spreading the entire commonVehiclesCountValues object separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...Object.values({ ...primitiveVehiclesCountWatchedValues, ...feesAndTaxesValues, ...preferences }),
  ]);

  useEffect(() => {
    setValue(FIELD_NAMES.APPROVED_LOAN_VALUE, roundValue(-approvedLoanValue));
  }, [approvedLoanValue, setValue]);

  useEffect(() => {
    setValue(FIELD_NAMES.PRICE_ALLOWED_VALUE, roundValue(priceAllowedValue));
  }, [priceAllowedValue, setValue]);

  useEffect(() => {
    setValue(FIELD_NAMES.NET_DOWN_PAYMENT, roundValue(netDownPayment));
  }, [netDownPayment, setValue]);

  useEffect(() => {
    if (areRequiredFieldsFilledIn) {
      dispatch(resetResetCount());
    }
  }, [dispatch, areRequiredFieldsFilledIn]);

  // Reset vehicles if the user has deleted some of the required input values
  useEffect(() => {
    if (vehicles?.length && !debouncedIsValid) {
      dispatch(resetVehicles());
    }
  }, [dispatch, vehicles?.length, debouncedIsValid]);

  useEffect(() => {
    if (addVehicleFlags.isFetching) setShowCreatedVehicleErrorMessage(false);
  }, [addVehicleFlags.isFetching]);

  useEffect(() => {
    if (!areRequiredFieldsFilledIn) dispatch(setIsModalsDisabled({ value: true, key: DisabledModals.ALI }));
    else dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.ALI }));
  }, [areRequiredFieldsFilledIn, dispatch]);

  return (
    <>
      <FormProvider {...methods}>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            handleSearchSubmit();
          }}
        >
          <Row>
            <ApprovedLoanCol lg={ColumnWidth.L} md={ColumnWidth.FULL}>
              <Summary />
            </ApprovedLoanCol>
            <ApprovedLoanCol lg={ColumnWidth.XXS} md={ColumnWidth.M} xs={ColumnWidth.FULL}>
              <PanelWithFields
                title={t('components.feesAndTaxes.title')}
                fieldsConfig={FEES_AND_TAXES_FIELDS}
                useTouchedFields={true}
              />
            </ApprovedLoanCol>
            <ApprovedLoanCol lg={ColumnWidth.XXXS} md={ColumnWidth.M} xs={ColumnWidth.FULL}>
              {preferencesFields && (
                <PanelWithFields title={t('components.preferences.title')} fieldsConfig={preferencesFields} />
              )}
            </ApprovedLoanCol>
          </Row>
          <Row>
            <ApprovedLoanCol xs={ColumnWidth.FULL}>
              <VehicleSearchTable
                tableName={TableName.ALI}
                tablePlaceholderType={tablePlaceholderType}
                data={vehicles || []}
                reportDataSelector={getApprovedLoanInputReportData}
                tableSubmitCallback={handleSearchSubmit}
                defaultPlaceholderTitle={DEFAULT_PLACEHOLDER_TITLE}
                previewPlaceholder={previewPlaceholder}
                isTableActionsDisabled={!vehicles?.length}
                isTableView={isTableView}
                setIsTableView={setIsTableView}
                fetchingVehicleFlagsSelector={getVehicleFetchingFlags}
                areRequiredFieldsFilledIn={areRequiredFieldsFilledIn}
                notAvailableVehicles={notAvailableVehicles}
                notFoundVehicles={notFoundVehicles}
                isFilterByStockNumber
                showNoAddedVehicleErrorMessage={
                  !!vehicles?.length &&
                  createdUserVehicleTableName === TableName.ALI &&
                  showCreatedVehicleErrorMessage &&
                  !isFetching &&
                  !vehicles.map((vehicle) => vehicle.id).includes(createdVehicle.id)
                }
              />
            </ApprovedLoanCol>
          </Row>
        </Form>
      </FormProvider>
    </>
  );
};
